import { Button, Dropdown } from '@ibsys/kit';
import { ChevronDownIcon, PlayIcon } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePipelineScope } from '@/entities/expert-report';
import {
  CONVERSATION_PIPELINE_OPTIONS,
  EXPERT_REPORT_GENERATION_PIPELINE,
  type ConversationPipeline,
  type ExpertReportPipelineScope,
} from '@/entities/search';
import { buildPipelineScopeItems, type PipelineMenuItem } from './model/pipelineScopeMenu';

type Props = {
  /** Есть ли что запускать: выбраны строки или включено «выбрать все» */
  hasSelection: boolean;
  /** Запуск обработки уже идёт — повторный запуск заблокирован */
  isPending: boolean;
  /** Запуск выбранной обработки по всей выбранной выборке, у генерации заключений — с областью */
  onRun: (pipeline: ConversationPipeline, scope?: ExpertReportPipelineScope) => void;
  className?: string;
};

/**
 * Блок массовых действий над диалогами: выпадающий список обработок
 * из общего перечня {@link CONVERSATION_PIPELINE_OPTIONS}.
 * Без выбора и во время запроса — заблокирован.
 *
 * У генерации экспертных заключений вместо нажатия — каскадное подменю выбора области запуска:
 * заключения, затем узлы их структуры вглубь до конечных блоков.
 */
export const PipelineActions: FC<Props> = ({ hasSelection, isPending, onRun, className }) => {
  const { t } = useTranslation(['dialogs', 'common']);

  // Дерево областей приходит одним ответом на весь каскад и нужно только раскрытому блоку
  // действий — вместе с ним компонент и монтируется, поэтому запрос уходит при раскрытии.
  const { data: scope, isLoading: isScopeLoading, isError: isScopeError } = usePipelineScope();

  const scopeItems = useMemo<PipelineMenuItem[]>(() => {
    const items = buildPipelineScopeItems(
      scope?.reports ?? [],
      {
        allReports: t('dialogs:dialogListSection.runPipeline.scope.allReports'),
        wholeReport: t('dialogs:dialogListSection.runPipeline.scope.wholeReport'),
        wholeGroup: t('dialogs:dialogListSection.runPipeline.scope.wholeGroup'),
      },
      (selected) => onRun(EXPERT_REPORT_GENERATION_PIPELINE, selected),
    );

    // Запуск по всем заключениям области не требует и доступен сразу; про сами заключения,
    // пока их перечень не получен, меню говорит отдельной строкой.
    if (isScopeLoading) {
      return [...items, { type: 'label', key: 'scope-loading', title: t('common:loading') }];
    }
    if (isScopeError) {
      return [
        ...items,
        { type: 'label', key: 'scope-error', title: t('dialogs:dialogListSection.runPipeline.scope.loadError') },
      ];
    }
    return items;
  }, [scope, isScopeLoading, isScopeError, onRun, t]);

  const options = useMemo<PipelineMenuItem[]>(
    () =>
      CONVERSATION_PIPELINE_OPTIONS.map((option) =>
        option.value === EXPERT_REPORT_GENERATION_PIPELINE
          ? { type: 'item', key: option.value, title: t(option.labelKey), children: scopeItems }
          : { type: 'item', key: option.value, title: t(option.labelKey), onClick: () => onRun(option.value) },
      ),
    [scopeItems, onRun, t],
  );

  return (
    <Dropdown options={options}>
      <Button variant="secondary" size="sm" className={className} disabled={!hasSelection || isPending}>
        <PlayIcon className="size-4" />
        {t('dialogs:dialogListSection.runPipeline.actions.label')}
        <ChevronDownIcon className="size-4" />
      </Button>
    </Dropdown>
  );
};
